import React, { useState } from 'react';
import {
  PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer,
} from 'recharts';
import Icon from '../Icon';
import { Slider } from '../ui/slider';
import { ghostBtn, primaryBtn } from './ui';
import { SPOKES, WHEEL } from '../../lib/assessment';

const PINK = '#F028DF';

// Full spoke names, wrapped onto two lines when long so nothing is cut off.
const wrapLabel = (name) => {
  if (name.length <= 10) return [name];
  const words = name.split(' ');
  if (words.length === 1) return [name];
  const lines = [''];
  words.forEach((word) => {
    const i = lines.length - 1;
    if (!lines[i]) lines[i] = word;
    else if (lines.length < 2 && (lines[i] + ' ' + word).length > 12) lines.push(word);
    else lines[i] = `${lines[i]} ${word}`;
  });
  return lines;
};

const AngleTick = ({ payload, x, y, textAnchor }) => {
  const lines = wrapLabel(String(payload.value));
  const nudge = textAnchor === 'end' ? 4 : textAnchor === 'start' ? -4 : 0;
  return (
    <text x={x + nudge} y={y} textAnchor={textAnchor} fill="#686C78" fontSize={10}>
      {lines.map((line, i) => (
        <tspan key={line} x={x + nudge} dy={i === 0 ? (lines.length > 1 ? -4 : 0) : 12}>{line}</tspan>
      ))}
    </text>
  );
};

// The wheel is how someone feels about each part of their life — never a score.
export const WellbeingWheel = ({ wheel, latest, checkins = [], checkinDue, onCheckin, busy }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => ({ ...(latest || wheel || {}) }));
  const current = latest || null;

  const data = SPOKES.map((spoke) => ({
    spoke,
    first: Number(wheel?.[spoke] ?? 0),
    latest: current ? Number(current[spoke] ?? 0) : null,
  }));

  const submit = async () => {
    await onCheckin(draft);
    setOpen(false);
  };

  return (
    <div className="bg-surface p-space-lg lg:p-space-xl rounded-lg shadow-sm" data-testid="wellbeing-wheel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-space-sm mb-space-md">
        <div>
          <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">HOW LIFE FEELS RIGHT NOW</span>
          <h2 className="font-headline-sm text-headline-sm uppercase text-outline">My Wellbeing Wheel</h2>
        </div>
        {checkinDue && onCheckin ? (
          <button type="button" onClick={() => setOpen(true)} className={primaryBtn} data-testid="wheel-checkin-open">
            <Icon name="event_available" className="text-[18px]" />
            <span>{WHEEL.monthly_checkin.prompt}</span>
          </button>
        ) : null}
      </div>

      <div className="w-full h-[320px] sm:h-[380px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="56%" margin={{ top: 16, right: 4, bottom: 16, left: 4 }}>
            <PolarGrid stroke="#DCDAE2" />
            <PolarAngleAxis dataKey="spoke" tick={AngleTick} />
            <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
            {current ? (
              <Radar name="First" dataKey="first" stroke={PINK} strokeOpacity={0.35} fill={PINK} fillOpacity={0.10} />
            ) : (
              <Radar name="First" dataKey="first" stroke={PINK} fill={PINK} fillOpacity={0.28} />
            )}
            {current ? (
              <Radar name="Latest" dataKey="latest" stroke={PINK} fill={PINK} fillOpacity={0.30} />
            ) : null}
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <p className="font-body-sm text-body-sm text-on-surface-variant leading-relaxed mt-space-md" data-testid="wheel-caption">{WHEEL.caption}</p>

      {checkins.length ? (
        <div className="mt-space-lg pt-space-md border-t border-surface-container" data-testid="wheel-checkin-history">
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant font-bold block mb-space-xs">Past check-ins</span>
          <ul className="flex flex-col gap-space-xs">
            {checkins.map((c) => (
              <li key={c.id} className="font-body-sm text-body-sm text-on-surface-variant">
                {new Date(c.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {open ? (
        <div className="mt-space-lg pt-space-md border-t border-surface-container" data-testid="wheel-checkin-form">
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant font-bold block mb-space-md">{WHEEL.monthly_checkin.prompt}</span>
          <div className="flex flex-col gap-space-lg">
            {SPOKES.map((spoke) => (
              <div key={spoke}>
                <div className="flex items-center justify-between mb-space-xs">
                  <span className="font-title-md text-title-md text-outline font-bold">{spoke}</span>
                  <span className="font-label-md text-label-md text-surface-tint font-bold">{draft[spoke] ?? 5}</span>
                </div>
                <Slider
                  min={0} max={10} step={1}
                  value={[draft[spoke] ?? 5]}
                  onValueChange={([v]) => setDraft((d) => ({ ...d, [spoke]: v }))}
                  aria-label={spoke}
                  data-testid={`checkin-slider-${spoke}`}
                />
              </div>
            ))}
          </div>
          <div className="mt-space-lg flex gap-space-sm">
            <button type="button" onClick={submit} disabled={busy} className={primaryBtn} data-testid="wheel-checkin-save">
              <Icon name="check" className="text-[18px]" /> <span>Save my check-in</span>
            </button>
            <button type="button" onClick={() => setOpen(false)} className={ghostBtn} data-testid="wheel-checkin-cancel">Cancel</button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WellbeingWheel;
